import Vue from 'vue';

import filesApi from '../api/files';
import { FileGalleryEvents } from '../constants/file';
import { IFilesService } from './types';

const eventBus = new Vue();

const requestsMap: Record<string, Record<string, { maxRequests: number; requests: any }>> = {
  assets: {
    search: {
      maxRequests: 1,
      requests: [],
    },
  },
  formAttributes: {
    search: {
      maxRequests: 1,
      requests: [],
    },
    getFileForm: {
      maxRequests: 1,
      requests: [],
    },
  },
};

/**
 * Cancels the oldest request for the given request name, if we already have
 * the max number of requests
 *
 * @param namespace - the namespace to check the request config under
 * @param requestName - the name of the request to check for
 */
const abortRequestBeforeNext = (namespace: string, requestName: string): void => {
  // If the config for the namespace or request does not exist, do nothing
  if (!requestsMap[namespace] || !requestsMap[namespace][requestName]) {
    return;
  }

  const requestConfig = requestsMap[namespace][requestName];

  if (
    requestConfig.requests.length &&
    requestConfig.requests.length === requestConfig.maxRequests
  ) {
    const oldestRequest = requestConfig.requests.shift(); // Remove it from the array
    oldestRequest.abort();
  }
};

/**
 * Checks if the oldest request should be canceled
 *
 * @param namespace - the namespace to check under
 * @param requestName - the name of the request to check
 */
const addRequest = (namespace: string, requestName: string) => (request: any): void => {
  // If the config for the namespace or request does not exist, do nothing
  if (!requestsMap[namespace] || !requestsMap[namespace][requestName]) {
    return;
  }
  // Abort the oldest if needed
  abortRequestBeforeNext(namespace, requestName);

  // Add our new request to the array
  requestsMap[namespace][requestName].requests.push(request);
};

export class FilesService implements IFilesService {
  /**
   * Searches for files
   *
   * @param {string} requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param config - search config
   * @param config.top - number of elements per page
   * @param config.skip - number of elements to skip
   * @param config.filter - filters on properties of an file
   * @param config.attributes - comma delimited list of attribute fieldNames
   * @param config.sortBy - comma delimited list of sort configs
   * @returns the api response or error
   */
  async search(
    requestNamespace: string,
    searchConfig: { top: number; skip: number; filter: string; attributes: string; sortBy: string }
  ) {
    return filesApi.search(searchConfig, addRequest(requestNamespace, 'search'));
  }

  /**
   * Gets the form for a file
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param fileId - the ID of the file
   * @returns the api response or error
   */
  async getFilesForm(requestNamespace: string, filesIds: string) {
    return filesApi.getFilesForm(filesIds, addRequest(requestNamespace, 'getFileForm'));
  }

  /**
   * Gets all attributes for a file
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param fileId - the ID of the file
   * @returns the api response or error
   */
  async getFileAttributes(requestNamespace: string, fileId: string) {
    return filesApi.getFileAttributes(fileId, addRequest(requestNamespace, 'getFileAttributes'));
  }

  /**
   * Creates a file
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param file - the file to create
   * @returns the api response or error
   */
  async createFile(requestNamespace: string, file: any) {
    return filesApi.createFile(file, addRequest(requestNamespace, 'createFile'));
  }

  /**
   * Updates multiple files' attributes
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param itemIds - ids if files to update
   * @param attributes - the attributes to update
   * @param taskName - the task to execute the update
   * @returns the api response or error
   */
  async updateFiles(
    requestNamespace: string,
    itemIds: string[],
    attributes: any[],
    taskName: string | null
  ) {
    return filesApi.updateFiles(
      itemIds,
      attributes,
      taskName,
      addRequest(requestNamespace, 'updateFiles')
    );
  }

  /**
   * Deletes a file
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param fileId - the ID of the file
   * @returns the api response or error
   */
  async deleteFile(requestNamespace: string, fileId: string) {
    return filesApi.deleteFile(fileId, addRequest(requestNamespace, 'deleteFile'));
  }

  /**
   * Gets the details of the file with the selected attributes
   *
   * @param requestNamespace - the name to execute the request under. Will
   * be used to limit by max concurrent requests
   * @param fileId - the ID of the folder
   * @param attributes names of the attributes to select
   * @returns the api response or error
   */
  async getFileById(requestNamespace: string, fileId: string, attributes: string) {
    return filesApi.getFileById(fileId, attributes, addRequest(requestNamespace, 'getFileById'));
  }

  /**
   * Gets the global form for all files
   *
   * @returns the api response or error
   */
  async getGlobalForm(requestNamespace: string) {
    return filesApi.getGlobalForm(addRequest(requestNamespace, 'getGlobalForm'));
  }

  /**
   * Get media url by media id
   * @param requestNamespace
   * @param fileId
   */
  async getMediaUrlById(requestNamespace: string, fileId: string) {
    return filesApi.getMediaUrlById(fileId, addRequest(requestNamespace, 'getMediaUrlById'));
  }

  /**
   * Emits an event
   *
   * @param eventName - the event to emit
   * @param args - arguments to pass to listeners
   * @private
   */
  private _$emit(eventName: string, ...args: any[]) {
    eventBus.$emit(eventName, ...args);
  }

  /**
   * Attaches a listener
   *
   * @param event - the event to listen to
   * @param callback - the callback to invoke when the event is received
   */
  $on(event: string | string[], callback: Function) {
    eventBus.$on(event, callback);
  }

  /**
   * Removes a listener
   *
   * @param event - the event to stop listening to
   * @param callback - the callback to remove
   */
  $off(event: string | string[], callback: Function) {
    eventBus.$off(event, callback);
  }

  /**
   * Emits the event to refresh the current search
   */
  refreshSearch(): void {
    this._$emit(FileGalleryEvents.REFRESH_SEARCH);
  }
}
